import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Send, MessageSquare, Check, CheckCheck } from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface MessageRow {
  id: string;
  message: string;
  status: string;
  created_at: string;
  name: string;
  email: string;
}
interface Reply {
  id: string;
  message_id: string;
  reply: string;
  created_at: string;
  read_at: string | null;
}
type Bubble =
  | { kind: "user"; id: string; text: string; at: string }
  | { kind: "admin"; id: string; text: string; at: string; read: boolean };

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  if (isToday(d)) return format(d, "p");
  if (isYesterday(d)) return "Yesterday " + format(d, "p");
  return format(d, "MMM d, p");
};

const MyMessages = () => {
  const { user, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    if (!user) return;
    const { data: msgs } = await supabase
      .from("messages")
      .select("id, message, status, created_at, name, email")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true });
    const list = (msgs ?? []) as MessageRow[];
    setMessages(list);
    if (list.length) {
      const ids = list.map((m) => m.id);
      const { data: reps } = await supabase
        .from("message_replies")
        .select("id, message_id, reply, created_at, read_at")
        .in("message_id", ids)
        .order("created_at", { ascending: true });
      const repList = (reps ?? []) as Reply[];
      setReplies(repList);
      // Mark unread replies as read
      const unread = repList.filter((r) => !r.read_at).map((r) => r.id);
      if (unread.length) {
        await supabase
          .from("message_replies")
          .update({ read_at: new Date().toISOString() })
          .in("id", unread);
      }
    } else {
      setReplies([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    load();
    const ch = supabase
      .channel("my-messages-chat")
      .on("postgres_changes", { event: "*", schema: "public", table: "message_replies" }, load)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "messages", filter: `user_id=eq.${user.id}` },
        load,
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const bubbles: Bubble[] = useMemo(() => {
    const u: Bubble[] = messages.map((m) => ({
      kind: "user",
      id: "m-" + m.id,
      text: m.message,
      at: m.created_at,
    }));
    const a: Bubble[] = replies.map((r) => ({
      kind: "admin",
      id: "r-" + r.id,
      text: r.reply,
      at: r.created_at,
      read: !!r.read_at,
    }));
    return [...u, ...a].sort((x, y) => +new Date(x.at) - +new Date(y.at));
  }, [messages, replies]);

  // Auto-scroll on new bubble
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [bubbles.length]);

  const send = async () => {
    if (!user || !text.trim()) return;
    setSending(true);
    const body = text.trim();
    const profileName =
      user.user_metadata?.full_name || user.email?.split("@")[0] || "Customer";
    const { error } = await supabase.from("messages").insert({
      user_id: user.id,
      name: profileName,
      email: user.email ?? "",
      message: body,
    });
    setSending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setText("");
  };

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth?redirect=/my-messages" replace />;

  const lastUserBubbleId = [...bubbles].reverse().find((b) => b.kind === "user")?.id;

  return (
    <section className="container py-4 md:py-8 max-w-3xl">
      <div className="flex items-center gap-2 mb-3">
        <Button asChild variant="ghost" size="icon" className="md:hidden">
          <Link to="/" aria-label="Back"><ArrowLeft className="h-5 w-5" /></Link>
        </Button>
        <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex">
          <Link to="/contact"><ArrowLeft className="h-4 w-4" /> Back</Link>
        </Button>
        <div className="flex-1">
          <h1 className="font-display text-xl md:text-2xl font-bold leading-tight">Messages</h1>
          <p className="text-xs text-muted-foreground">Chat with our support team</p>
        </div>
      </div>

      <div className="rounded-2xl border bg-card overflow-hidden flex flex-col h-[calc(100vh-180px)] md:h-[70vh] shadow-sm">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/40">
          <div className="h-9 w-9 rounded-full bg-gradient-primary text-primary-foreground grid place-items-center text-sm font-bold">
            S
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm leading-tight">Support team</p>
            <p className="text-[11px] text-muted-foreground">Typically replies within a few hours</p>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2 bg-muted/20">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-2/3" />
              <Skeleton className="h-10 w-1/2 ml-auto" />
              <Skeleton className="h-10 w-2/3" />
            </div>
          ) : bubbles.length === 0 ? (
            <div className="h-full grid place-items-center text-center px-6">
              <div>
                <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground text-sm">
                  Send your first message to start a conversation.
                </p>
              </div>
            </div>
          ) : (
            bubbles.map((b, i) => {
              const prev = bubbles[i - 1];
              const showTime =
                !prev ||
                prev.kind !== b.kind ||
                +new Date(b.at) - +new Date(prev.at) > 5 * 60 * 1000;
              const isUser = b.kind === "user";
              const showTicks = isUser && b.id === lastUserBubbleId;
              // A user message is "delivered+seen" if any admin reply came after it
              const seen =
                showTicks &&
                replies.some((r) => +new Date(r.created_at) > +new Date(b.at));
              return (
                <div key={b.id} className="animate-fade-in">
                  {showTime && (
                    <p
                      className={cn(
                        "text-[10px] text-muted-foreground mb-1 mt-2",
                        isUser ? "text-right pr-2" : "pl-2",
                      )}
                    >
                      {fmtTime(b.at)}
                    </p>
                  )}
                  <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "max-w-[80%] md:max-w-[70%] rounded-2xl px-3.5 py-2 text-sm shadow-sm whitespace-pre-wrap break-words",
                        isUser
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-background border rounded-bl-md",
                      )}
                    >
                      {b.text}
                      {showTicks && (
                        <span className="inline-flex items-center ml-2 align-middle opacity-80">
                          {seen ? (
                            <CheckCheck className="h-3.5 w-3.5" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Composer */}
        <div className="border-t p-2.5 bg-background">
          <div className="flex items-end gap-2">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Type a message…"
              rows={1}
              maxLength={2000}
              className="resize-none min-h-[44px] max-h-32 rounded-2xl"
            />
            <Button
              onClick={send}
              disabled={sending || !text.trim()}
              size="icon"
              className="h-11 w-11 shrink-0 rounded-full"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MyMessages;
